import axios from 'axios';
import { BASE_URL } from '../Constants';

// Event API service class
class EventAPIService {
  constructor() {
    this.baseURL = BASE_URL;
    this.token = localStorage.getItem('accessToken');

    // Setup axios interceptor for authentication
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.token ? `Bearer ${this.token}` : ''
      }
    });

    // Request interceptor to refresh token if needed
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          localStorage.removeItem('accessToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Get all events for a company
  async getEvents(companyId, params = {}) {
    try {
      const response = await this.api.get(`/events/company/${companyId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw this.handleError(error);
    }
  }

  // Get single event by ID
  async getEvent(eventId) {
    try {
      const response = await this.api.get(`/events/${eventId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event:', error);
      throw this.handleError(error);
    }
  }

  // Create new event
  async createEvent(eventData) {
    try {
      const response = await this.api.post('/events', eventData);
      return response.data;
    } catch (error) {
      console.error('Error creating event:', error);
      throw this.handleError(error);
    }
  }

  // Update existing event
  async updateEvent(eventId, eventData) {
    try {
      const response = await this.api.put(`/events/${eventId}`, eventData);
      return response.data;
    } catch (error) {
      console.error('Error updating event:', error);
      throw this.handleError(error);
    }
  }

  // Delete event
  async deleteEvent(eventId) {
    try {
      const response = await this.api.delete(`/events/${eventId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting event:', error);
      throw this.handleError(error);
    }
  }

  // Get event registrations
  async getEventRegistrations(eventId, params = {}) {
    try {
      const response = await this.api.get(`/events/${eventId}/registrations`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching registrations:', error);
      throw this.handleError(error);
    }
  }

  // Register for event (public endpoint)
  async registerForEvent(eventId, registrationData) {
    try {
      const response = await this.api.post(`/events/${eventId}/register`, registrationData);
      return response.data;
    } catch (error) {
      console.error('Error registering for event:', error);
      throw this.handleError(error);
    }
  }

  // Update registration status
  async updateRegistrationStatus(eventId, registrationId, status) {
    try {
      const response = await this.api.patch(`/events/${eventId}/registrations/${registrationId}`, {
        status
      });
      return response.data;
    } catch (error) {
      console.error('Error updating registration status:', error);
      throw this.handleError(error);
    }
  }

  // Get event analytics
  async getEventAnalytics(eventId) {
    try {
      const response = await this.api.get(`/events/${eventId}/analytics`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event analytics:', error);
      throw this.handleError(error);
    }
  }

  // Upload event image
  async uploadEventImage(file, eventId = null) {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (eventId) formData.append('eventId', eventId);

      const response = await this.api.post('/events/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading event image:', error);
      throw this.handleError(error);
    }
  }

  // Send bulk emails to registrations
  async sendBulkEmails(eventId, registrationIds, emailData) {
    try {
      const response = await this.api.post(`/events/${eventId}/send-emails`, {
        registrationIds,
        ...emailData
      });
      return response.data;
    } catch (error) {
      console.error('Error sending bulk emails:', error);
      throw this.handleError(error);
    }
  }

  // Export registrations
  async exportRegistrations(eventId, format = 'csv', filters = {}) {
    try {
      const response = await this.api.get(`/events/${eventId}/export`, {
        params: { format, ...filters },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `event-registrations-${eventId}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Export downloaded successfully' };
    } catch (error) {
      console.error('Error exporting registrations:', error);
      throw this.handleError(error);
    }
  }

  // Get event statistics summary
  async getEventStats(companyId) {
    try {
      const response = await this.api.get(`/events/company/${companyId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event stats:', error);
      throw this.handleError(error);
    }
  }

  // Search events
  async searchEvents(companyId, query, filters = {}) {
    try {
      const response = await this.api.get(`/events/company/${companyId}/search`, {
        params: { query, ...filters }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching events:', error);
      throw this.handleError(error);
    }
  }

  // Get public event data (for landing pages)
  async getPublicEvent(eventId) {
    try {
      const response = await axios.get(`${this.baseURL}/events/${eventId}/public`);
      return response.data;
    } catch (error) {
      console.error('Error fetching public event:', error);
      throw this.handleError(error);
    }
  }

  // Duplicate event
  async duplicateEvent(eventId, newTitle) {
    try {
      const response = await this.api.post(`/events/${eventId}/duplicate`, {
        title: newTitle
      });
      return response.data;
    } catch (error) {
      console.error('Error duplicating event:', error);
      throw this.handleError(error);
    }
  }

  // Toggle event status (active/inactive)
  async toggleEventStatus(eventId, status) {
    try {
      const response = await this.api.patch(`/events/${eventId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error toggling event status:', error);
      throw this.handleError(error);
    }
  }

  // Get event QR code
  async getEventQRCode(eventId) {
    try {
      const response = await this.api.get(`/events/${eventId}/qr-code`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'image/png' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching QR code:', error);
      throw this.handleError(error);
    }
  }

  // Handle API errors
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      switch (status) {
        case 400:
          return { message: data.message || 'Bad request. Please check your input.', status };
        case 401:
          return { message: 'Unauthorized. Please login again.', status };
        case 403:
          return { message: 'Access denied. You do not have permission for this action.', status };
        case 404:
          return { message: 'Resource not found.', status };
        case 409:
          return { message: data.message || 'Conflict. Resource already exists.', status };
        case 422:
          return { message: data.message || 'Validation failed. Please check your input.', status };
        case 500:
          return { message: 'Server error. Please try again later.', status };
        default:
          return { message: data.message || 'An unexpected error occurred.', status };
      }
    } else if (error.request) {
      // Network error
      return { message: 'Network error. Please check your internet connection.', status: 0 };
    } else {
      // Other error
      return { message: error.message || 'An unexpected error occurred.', status: -1 };
    }
  }

  // Mock data methods (for development/testing)
  getMockEvents() {
    return [
      {
        id: 1,
        title: 'Innovation Summit 2025',
        description: 'Join industry leaders, innovators, and visionaries for three days of groundbreaking insights, networking, and inspiration that will shape the future of the Kingdom.',
        shortDescription: 'Three days of innovation and networking',
        date: ['2025-03-15', '2025-03-17'],
        time: '09:00',
        endTime: '17:00',
        location: 'Riyadh, Saudi Arabia',
        venue: 'King Fahd Cultural Centre',
        capacity: 5000,
        registrations: 1247,
        vipRegistrations: 89,
        regularRegistrations: 1158,
        revenue: 185250,
        status: 'active',
        eventType: 'conference',
        category: 'technology',
        backgroundImage: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
        createdAt: '2024-12-01',
        updatedAt: '2024-12-20'
      },
      {
        id: 2,
        title: 'Digital Transformation Workshop',
        description: 'A comprehensive workshop on digital transformation strategies for modern businesses.',
        shortDescription: 'Hands-on digital transformation workshop',
        date: ['2025-04-10', '2025-04-12'],
        time: '10:00',
        endTime: '16:00',
        location: 'Dubai, UAE',
        venue: 'Emirates Business Center',
        capacity: 200,
        registrations: 156,
        vipRegistrations: 12,
        regularRegistrations: 144,
        revenue: 23400,
        status: 'active',
        eventType: 'workshop',
        category: 'business',
        backgroundImage: 'https://images.unsplash.com/photo-1559223607-a43c990c692c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
        createdAt: '2024-12-05',
        updatedAt: '2024-12-18'
      },
      {
        id: 3,
        title: 'Healthcare Innovation Symposium',
        description: 'Exploring the future of healthcare through technology and innovation.',
        shortDescription: 'Future of healthcare technology',
        date: ['2025-05-20', '2025-05-21'],
        time: '08:30',
        endTime: '17:30',
        location: 'Jeddah, Saudi Arabia',
        venue: 'Jeddah Medical Complex',
        capacity: 800,
        registrations: 423,
        vipRegistrations: 28,
        regularRegistrations: 395,
        revenue: 63450,
        status: 'draft',
        eventType: 'symposium',
        category: 'healthcare',
        backgroundImage: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
        createdAt: '2024-12-10',
        updatedAt: '2024-12-19'
      }
    ];
  }

  getMockRegistrations() {
    return [
      {
        id: 1,
        name: 'Ahmed Al-Rashid',
        email: 'ahmed.rashid@company.com',
        phone: '+966 50 123 4567',
        company: 'Tech Innovations Ltd',
        position: 'CTO',
        type: 'vip',
        vipPackages: ['Priority Seating', 'VIP Networking'],
        status: 'confirmed',
        registrationDate: '2024-12-15',
        paymentStatus: 'paid',
        amount: 850,
        notes: 'Requested vegetarian meal'
      },
      {
        id: 2,
        name: 'Sarah Johnson',
        email: 'sarah.johnson@innovate.com',
        phone: '+1 555 987 6543',
        company: 'Global Innovations',
        position: 'Head of Strategy',
        type: 'regular',
        vipPackages: [],
        status: 'confirmed',
        registrationDate: '2024-12-10',
        paymentStatus: 'paid',
        amount: 150,
        notes: ''
      },
      {
        id: 3,
        name: 'Mohammed bin Hassan',
        email: 'm.hassan@future-tech.sa',
        phone: '+966 55 555 1234',
        company: 'Future Tech Solutions',
        position: 'CEO',
        type: 'vip',
        vipPackages: ['Priority Seating', 'Meet & Greet'],
        status: 'pending',
        registrationDate: '2024-12-18',
        paymentStatus: 'pending',
        amount: 1250,
        notes: 'Special dietary requirements'
      },
      {
        id: 4,
        name: 'Lisa Chen',
        email: 'lisa.chen@techcorp.com',
        phone: '+86 138 1234 5678',
        company: 'TechCorp International',
        position: 'Innovation Director',
        type: 'regular',
        vipPackages: [],
        status: 'confirmed',
        registrationDate: '2024-12-05',
        paymentStatus: 'paid',
        amount: 100,
        notes: 'Early bird discount applied'
      },
      {
        id: 5,
        name: 'Omar Al-Mahmoud',
        email: 'omar.mahmoud@startups.sa',
        phone: '+966 56 789 0123',
        company: 'Saudi Startups Hub',
        position: 'Managing Partner',
        type: 'vip',
        vipPackages: ['VIP Networking', 'Meet & Greet'],
        status: 'confirmed',
        registrationDate: '2024-12-12',
        paymentStatus: 'paid',
        amount: 1050,
        notes: 'Networking priority requested'
      }
    ];
  }
}

// Export singleton instance
export default new EventAPIService();